import { LabelLinkField } from "@/components/LabelLinkField";
import { useActiveProject } from "@/features/project/ActiveProject";
import { CurrencyField } from "@/features/projects/cards/components/CurrencyField";
import { type ExtendedAppointment } from "@repo/api";
import { LightMode, Text } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  FlexDiv,
  Label,
  LabelField,
  LocalizedDate,
  Name,
  SectionContainer,
  TwoColumnSectionContainer,
  localizeDateAndTime,
  pixels,
  translatePaymentStatus,
  useSettings,
} from "@repo/ui";
import { hasItems, maskEmail, maskPhoneNumber } from "@repo/utils";
import isEmpty from "lodash/isEmpty";

import { isOpenSlot } from "./utils";

interface AppointmentDetailsProps {
  appointment: ExtendedAppointment;
}
export const AppointmentDetails = ({
  appointment,
}: AppointmentDetailsProps) => {
  const { customerMode, boka_config } = useSettings();
  const { dateFnsLocale } = useTranslation();
  const { opportunity } = useActiveProject();

  const email = appointment.customer?.email;
  const phoneNumber = appointment.customer?.phoneNumber;
  const isBooked = !isOpenSlot(
    appointment.reasonCode,
    appointment.orderNumber,
    appointment.type
  );
  const showFollowUpFields =
    Boolean(boka_config?.enable_boka_follow_up_integration) && isBooked;

  return (
    <SectionContainer>
      {appointment.number && (
        <Text textStyle="Body.S" color={LightMode.colourTextAndIcon3}>
          {t("label.appointment_with_number", {
            number: appointment.number,
          })}
        </Text>
      )}
      <TwoColumnSectionContainer>
        <LabelField
          id="appointment-id"
          editable={false}
          label={`${t("label.id")}/${t("label.order_id")}`}
          value={appointment.orderNumber ?? "-"}
        />

        {appointment.customer && (
          <LabelField
            id="appointment-customer"
            editable={false}
            label={t("label.customer")}
            value={
              <Name
                firstName={appointment.customer.firstName}
                lastName={appointment.customer.lastName}
              />
            }
          />
        )}

        {!isEmpty(email) && (
          <LabelField
            id="appointment-customer-email"
            editable={false}
            label={t("label.email")}
            value={customerMode ? maskEmail(email) : email}
          />
        )}

        {!isEmpty(phoneNumber) && (
          <LabelField
            id="appointment-customer-phone"
            editable={false}
            label={t("label.phone")}
            value={customerMode ? maskPhoneNumber(phoneNumber) : phoneNumber}
          />
        )}

        <LabelField
          id="booking-time"
          editable={false}
          label={t("label.booking_time")}
          value={
            appointment.bookingTime
              ? localizeDateAndTime(appointment.bookingTime, dateFnsLocale)
              : "-"
          }
        />

        {appointment.price !== 0 && (
          <CurrencyField
            id="appointment-price"
            editable={false}
            label={t("label.price")}
            value={appointment.price}
            currency={opportunity?.currency}
          />
        )}
        {appointment.paymentStatus && (
          <LabelField
            id="appointment-payment"
            editable={false}
            label={t("label.payment_status")}
            value={translatePaymentStatus(appointment.paymentStatus)}
          />
        )}

        {showFollowUpFields && (
          <>
            <LabelField
              id="appointment-previous-order-number"
              editable={false}
              label={t("label.previous_order_number")}
              value={appointment.previousOrderNumber?.toString() ?? "-"}
            />
            <LabelField
              id="appointment-followup-order-number"
              editable={false}
              label={t("label.followup_order_number")}
              value={appointment.followupOrderNumber?.toString() ?? "-"}
            />
          </>
        )}

        {appointment.videoLinkCoworker && (
          <LabelLinkField
            label={t("label.meeting_link_co_worker")}
            link={appointment.videoLinkCoworker}
          />
        )}
        {appointment.videoLinkCustomer && (
          <LabelLinkField
            label={t("label.meeting_link_customer")}
            link={appointment.videoLinkCustomer}
          />
        )}
      </TwoColumnSectionContainer>
      <div>
        <Label label={t("label.comments")} bold />
        {hasItems(appointment.comments)
          ? appointment.comments.map((comment) => (
              <FlexDiv key={comment.createdAt} gap={pixels.space8}>
                <Text textStyle="Body.S">
                  <LocalizedDate date={comment.createdAt} />
                </Text>
                <Text textStyle="Body.S">{comment.comment}</Text>
              </FlexDiv>
            ))
          : "-"}
      </div>
    </SectionContainer>
  );
};
